import type { QueryClient } from '@tanstack/react-query'
import { queryOptions } from '@tanstack/react-query'

import { api } from './api'
import { haptic } from './telegram'
import type { Day, PhotoAngle, Slot } from './types'

const factsStaleTime = 5_000

const routineStaleTime = 60_000

export const dayQuery = (date: string) =>
  queryOptions({
    queryKey: ['day', date],
    queryFn: () => api.day(date).then((response) => response.day),
    staleTime: factsStaleTime,
  })

export const historyQuery = (from: string, to: string) =>
  queryOptions({
    queryKey: ['history', from, to],
    queryFn: () => api.history(from, to).then((response) => response.days),
    staleTime: factsStaleTime,
  })

export const productsQuery = (filter: { search?: string; status?: string; active?: string } = {}) =>
  queryOptions({
    queryKey: ['products', filter.search ?? '', filter.status ?? '', filter.active ?? ''],
    queryFn: () => api.products(filter).then((response) => response.products),
    staleTime: routineStaleTime,
  })

export const productQuery = (id: string) =>
  queryOptions({
    queryKey: ['product', id],
    queryFn: () => api.product(id).then((response) => response.product),
    staleTime: routineStaleTime,
  })

export const stepsQuery = (slot?: Slot) =>
  queryOptions({
    queryKey: ['steps', slot ?? 'all'],
    queryFn: () => api.steps(slot).then((response) => response.steps),
    staleTime: routineStaleTime,
  })

export const conflictsQuery = () =>
  queryOptions({
    queryKey: ['conflicts'],
    queryFn: () => api.conflicts().then((response) => response.conflicts),
    staleTime: routineStaleTime,
  })

export const profileQuery = () =>
  queryOptions({
    queryKey: ['profile'],
    queryFn: () => api.profile().then((response) => response.profile),
    staleTime: routineStaleTime,
  })

export const flagsQuery = () =>
  queryOptions({
    queryKey: ['flags'],
    queryFn: () => api.flags().then((response) => response.flags),
    staleTime: routineStaleTime,
  })

export const checkinQuery = (type: 'weekly' | 'monthly') =>
  queryOptions({
    queryKey: ['checkin', type],
    queryFn: () => api.currentCheckin(type),
    staleTime: factsStaleTime,
  })

export const exportSummaryQuery = (from?: string, to?: string) =>
  queryOptions({
    queryKey: ['export', from ?? '', to ?? ''],
    queryFn: () => api.exportSummary(from, to).then((response) => response.summary),
    staleTime: factsStaleTime,
  })

export const connectionQuery = () =>
  queryOptions({
    queryKey: ['connection'],
    queryFn: () => api.connection(),
    staleTime: factsStaleTime,
  })

export const eventsQuery = () =>
  queryOptions({
    queryKey: ['mcp-events'],
    queryFn: () => api.events().then((response) => response.events),
    staleTime: factsStaleTime,
  })

export const settingsQuery = () =>
  queryOptions({
    queryKey: ['settings'],
    queryFn: () => api.settings(),
    staleTime: routineStaleTime,
  })

export const photosQuery = (angle?: PhotoAngle) =>
  queryOptions({
    queryKey: ['photos', angle ?? 'all'] as const,
    queryFn: () => api.photos(angle).then((response) => response.photos),
    staleTime: factsStaleTime,
  })

export type DayAction = {
  send: () => Promise<{ day: Day }>
  expect?: (day: Day) => Day
}

export function dayMutationHandlers(
  date: string,
  queryClient: QueryClient,
  toast: (text: string, tone?: 'ok' | 'error') => void,
) {
  return {
    mutationFn: (action: DayAction) => action.send(),
    onMutate: async (action: DayAction) => {
      haptic.mark()
      if (!action.expect) return { previous: undefined }
      await queryClient.cancelQueries({ queryKey: factKeys.day(date) })
      const previous = queryClient.getQueryData<Day>(factKeys.day(date))
      if (previous) queryClient.setQueryData(factKeys.day(date), action.expect(previous))
      return { previous }
    },
    onSuccess: (response: { day: Day }) => {
      queryClient.setQueryData(factKeys.day(date), response.day)
      void queryClient.invalidateQueries({ queryKey: factKeys.history })
    },
    onError: (error: Error, _action: DayAction, context?: { previous?: Day }) => {
      if (context?.previous) queryClient.setQueryData(factKeys.day(date), context.previous)
      haptic.fail()
      toast(error.message, 'error')
    },
  }
}

export const factKeys = {
  day: (date: string) => ['day', date] as const,
  history: ['history'] as const,
  photos: ['photos'] as const,
  export: ['export'] as const,
}
